import { Component, inject, OnInit } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { Bankverbindung, Kunde, Zahlungsempfaenger } from '@gsm/entities';
import { DynamicFormControlEvent, DynamicFormModel, DynamicFormOptionConfig, DynamicSelectModel } from '@ng-dynamic-forms/core';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { strictDynamicFormControlCustomEvent } from '../../helpers/custom-event.helper';
import { setModelDisabled } from '../../helpers/disable-model.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { getDynamicFormModelMap } from '../../helpers/model-map.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { EVENT_TYPE_IBAN_CHANGED } from '../shared-models/custom-events.model';
import { COMPARE_KUNDEID, COMPARE_NAMEN, DIREKT_AN_GESCHAEDIGTE_PERSON, mapKundenToOptions, NEU_EMPFAENGER, RECHNUNGSSTELLER, ZAHLUNGSEMPFAENGER_TEMPLATE_FACTORY } from './zahlungsempfaenger.config';
import { ZahlungsempfaengerModel } from './zahlungsempfaenger.model';

@Component({
  selector:    'gsm-zahlungsempfaenger',
  templateUrl: './zahlungsempfaenger.component.html',
  styleUrls:   ['./zahlungsempfaenger.component.scss'],
  imports:     [
    DynamicMaterialFormComponent
  ],
  standalone:  true
})
export class ZahlungsempfaengerComponent extends CustomDynamicFormControlComponent<ZahlungsempfaengerModel> implements OnInit {
  private formService = inject(CustomDynamicFormService);

  formModel: DynamicFormModel;
  formGroup: UntypedFormGroup;

  private _isUpdating = false;
  private _modelMap: Record<string, DynamicSelectModel<any>>;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  private get modelMap(): Record<string, DynamicSelectModel<any>> {
    return this._modelMap || (this._modelMap = getDynamicFormModelMap(this.formModel as DynamicSelectModel<any>[]));
  }

  protected onModelChange() {
    const removeRechnungssteller = this.model.removeRechnungssteller;
    const addDirektAnGeschPerson = this.model.addDirektAnGeschPerson;
    let {kunden} = this.model;
    const immediateValidation = this.model.additional?.immediateValidation;

    // in case the component is shown in the history, kunden will be null as we do not ask for data from the backend,
    // therefore the mapping for kunden is happening here
    if (!kunden && this.model.value) {
      kunden = [this.model.value];
      kunden.forEach(kunde => (kunde.bankVerbindungList = [this.model.value.bankverbindung]));
    }

    this.formModel = this.formService.fromJSON(
      ZAHLUNGSEMPFAENGER_TEMPLATE_FACTORY(immediateValidation, kunden, removeRechnungssteller, addDirektAnGeschPerson)
    );
    this.formGroup = this.formService.createFormGroup(this.formModel);

    // reset cached modelMap
    this._modelMap = null;

    if (this.model.disabled) {
      setModelDisabled(this.formModel, true);
    }

    this.model.kundenChange$.pipe(this.untilNextModel)
      .subscribe(() => {
        this.modelMap.zahlungsempfaenger.options = mapKundenToOptions(
          this.model.kunden,
          this.model.removeRechnungssteller,
          addDirektAnGeschPerson
        );
        if (!this._isUpdating) {
          this._updateFormFromValue();
        }
      });

    this._updateFormFromValue();

    if (immediateValidation) {
      this.formGroup.markAllAsTouched();
    }

    this._emitChange();
  }

  protected onValueChange() {
    if (!this._isUpdating) {
      this._updateFormFromValue();
      this._emitChange();
    }
  }

  protected onDisabledChange(): void {
    setModelDisabled(this.formModel, this.model.disabled);
    super.onDisabledChange();
  }

  onFormChange(event: DynamicFormControlEvent): void {
    if (!this._isUpdating) {
      const zahlungsempfaengerChanged: boolean = !!event.model && event.model.id === 'zahlungsempfaenger';
      const ibanChanged: boolean = !!event.model && event.model.id === 'iban';

      if (zahlungsempfaengerChanged) {
        this.formGroup.get('bankverbindung')
          .setValue(null);
        this.formGroup.get('externalEmpfaenger')
          .reset();
        this.formGroup.get('existingEmpfaenger')
          .reset();
      }

      this._updateValueFromForm();

      if (ibanChanged) {
        const {zahlungsempfaenger} = this.modelMap;
        const selectedKunde = this._getSelectedKunde(zahlungsempfaenger);
        let empfaenger = null;
        if (selectedKunde === NEU_EMPFAENGER) {
          empfaenger = this.formGroup.get('externalEmpfaenger');
        } else if (!this.model.value.isKunde) {
          empfaenger = this.formGroup.get('existingEmpfaenger');
        }
        if (empfaenger) {
          const empfaengerValue = empfaenger.value;
          if (!empfaengerValue.bic) {
            this._isUpdating = true;
            this.customEvent.emit(
              strictDynamicFormControlCustomEvent(EVENT_TYPE_IBAN_CHANGED, {
                detail: empfaenger
              })
            );
            this._isUpdating = false;
          }
        }
      }

      if (zahlungsempfaengerChanged) {
        this._updateFormFromValue(false);
      }

      this._emitChange();
    }
  }

  private _emitChange() {
    this.group.controls[this.model.id].setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _updateValueFromForm() {
    this._isUpdating = true;
    this.model.value = this._getModelValueFromForm();
    this._isUpdating = false;
  }

  private _getModelValueFromForm(): Zahlungsempfaenger {
    const selectedEmpfaenger = this.formGroup.get('zahlungsempfaenger').value as Kunde;
    if (selectedEmpfaenger) {
      switch (selectedEmpfaenger.kundeId) {
        case NEU_EMPFAENGER.kundeId: {
          const {
            vorname,
            nachname,
            iban,
            bic
          } = this.formGroup.get('externalEmpfaenger').value;
          return {
            vorname,
            nachname,
            bankverbindung: {
              iban,
              bic
            }
          } as Zahlungsempfaenger;
        }
        case RECHNUNGSSTELLER.kundeId: {
          return {rechnungssteller: true} as Zahlungsempfaenger;
        }
        case DIREKT_AN_GESCHAEDIGTE_PERSON.kundeId: {
          return {direktAnGeschPerson: true} as Zahlungsempfaenger;
        }
        default: {
          const {
            kundeId,
            isKunde,
            vorname,
            nachname,
            namenZusatz,
            adresse,
            geburtsDatum
          } = selectedEmpfaenger;

          // if it's a kunde, get bankverbindung from bankverbindung select, otherwise from existingEmpfaenger form group
          const bankverbindung = isKunde ? this.formGroup.get('bankverbindung').value : this.formGroup.get('existingEmpfaenger').value;
          return filterEmptyProps({
            kundeId,
            isKunde,
            vorname,
            nachname,
            namenZusatz,
            adresse,
            geburtsDatum,
            bankverbindung
          }) as Zahlungsempfaenger;
        }
      }
    }
    return null;
  }

  private _updateFormFromValue(updateZahlungsempfaenger = true) {
    this._isUpdating = true;

    const {
      zahlungsempfaenger,
      bankverbindung,
      externalEmpfaenger,
      existingEmpfaenger
    } = this.modelMap;
    const selectedKunde = this._getSelectedKunde(zahlungsempfaenger);

    if (updateZahlungsempfaenger) {
      this.formGroup.get('zahlungsempfaenger')
        .setValue(selectedKunde);
    }

    if (!selectedKunde) {
      existingEmpfaenger.hidden = true;
    }

    if (!this.model.value || this.model.value.rechnungssteller || this.model.value.direktAnGeschPerson) {
      // if no value is present, or it's a rechnungssteller, hide all banking fields
      bankverbindung.hidden = true;
      externalEmpfaenger.hidden = true;
      existingEmpfaenger.hidden = true;
      this.formGroup.get('bankverbindung')
        .setValue(null);
    } else if (this.model.value) {
      if (selectedKunde === NEU_EMPFAENGER) {
        // it's an external empfaenger
        bankverbindung.hidden = true;
        externalEmpfaenger.hidden = false;
        existingEmpfaenger.hidden = true;

        const {
          vorname,
          nachname,
          bankverbindung: {
                            iban,
                            bic
                          } = {
            bic:  '',
            iban: ''
          }
        } = this.model.value;
        this.formGroup.get('externalEmpfaenger')
          .patchValue({
            vorname,
            nachname,
            iban,
            bic
          });
      } else if (!this.model.value.isKunde) {
        // it's an existing person who's not a kunde
        bankverbindung.hidden = true;
        externalEmpfaenger.hidden = true;
        existingEmpfaenger.hidden = false;

        const {
          bankverbindung: {
                            iban,
                            bic
                          } = {
            bic:  '',
            iban: ''
          }
        } = this.model.value;
        this.formGroup.get('existingEmpfaenger')
          .patchValue({
            iban,
            bic
          });
      } else {
        // it's a known Kunde
        bankverbindung.hidden = false;
        externalEmpfaenger.hidden = true;
        existingEmpfaenger.hidden = true;

        if (selectedKunde?.bankVerbindungList) {
          (bankverbindung as DynamicSelectModel<Bankverbindung>).options = mapBankverbindungenToOptions(selectedKunde.bankVerbindungList);
          const selectedBankverbindung = this._getSelectedBankverbindung(bankverbindung);
          this.formGroup.get('bankverbindung')
            .setValue(selectedBankverbindung);
        } else {
          (bankverbindung as DynamicSelectModel<Bankverbindung>).options = [];
          this.formGroup.get('bankverbindung')
            .setValue(null);
        }
      }
    }

    this._isUpdating = false;
  }

  private _getErrors(): ValidationErrors {
    const invalid = formElementsInvalid(this.formGroup, this.formModel);
    return invalid ? {zahlungsempfaenger: true} : null;
  }

  private _getSelectedKunde(kunden: DynamicSelectModel<Kunde>): Kunde {
    if (!this.model.value) {
      return null;
    }
    if (this.model.value.rechnungssteller) {
      return RECHNUNGSSTELLER;
    }
    if (this.model.value.direktAnGeschPerson) {
      return DIREKT_AN_GESCHAEDIGTE_PERSON;
    }
    if (!this.model.value.kundeId) {
      const selectedKunde = kunden.options.find(({value}) => COMPARE_NAMEN(value, this.model.value))?.value;
      return selectedKunde || NEU_EMPFAENGER;
    }
    return kunden.options.find(({value}) => COMPARE_KUNDEID(value, this.model.value))?.value;
  }

  private _getSelectedBankverbindung(bankverbindungen: DynamicSelectModel<Bankverbindung>) {
    return this.model.value.bankverbindung
      ? bankverbindungen.options.find(({value}) => value.bankverbindungId === this.model.value.bankverbindung.bankverbindungId)?.value
      : null;
  }
}

function mapBankverbindungenToOptions(bankVerbindungen: Bankverbindung[] = []): DynamicFormOptionConfig<Bankverbindung>[] {
  return bankVerbindungen.map(bankverbindung => {
    const {
      iban = '',
      bic = ''
    } = bankverbindung;
    return {
      label: `${iban && 'IBAN: '}${iban}${iban && bic && ', '}${bic && 'BIC: '}${bic}`,
      value: bankverbindung
    };
  });
}
